import { gql, NetworkStatus } from '@apollo/client';
import React, { FunctionComponent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import urljoin from 'url-join';
import {
  LeaderboardFundraisersListQuery,
  PageStatus,
} from '../../../types/__generated__';
import config from '../../client/config';
import useFullPageQuery from '../../hooks/useFullPageQuery';
import { makePhotoUrlFromAbsoluteUrl } from '../../utils/makePhotoUrl';
import BenefactorListItem from '../BenefactorListItem/BenefactorListItem';
import BenefactorListItemSkeleton from '../BenefactorListItem/BenefactorListItemSkeleton';
import LoadMoreButton from '../LoadMoreButton/LoadMoreButton';
import PageSection from '../PageSection/PageSection';
import styles from './FundraisersList.scss';

const DOCUMENT = gql`
  query LeaderboardFundraisersList(
    $after: String
    $type: PageType!
    $slug: Slug
    $id: ID
  ) {
    page(type: $type, slug: $slug, id: $id) {
      id
      allowFundraising
      fundraisers: leaderboard(type: FUNDRAISERS, first: 5, after: $after) {
        edges {
          node {
            id
            slug
            status
            owner {
              id
              avatar
              name
            }
            donations(first: 1) {
              totalCount
            }
            donationSummary {
              totalAmount {
                value
                currencyCode
              }
            }
            targetWithCurrency {
              value
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
        totalCount
      }
    }
  }
`;

const LeaderboardFundraiserList: FunctionComponent = () => {
  const { t } = useTranslation('common', { useSuspense: false });
  const { loading, error, data, fetchMore, networkStatus } = useFullPageQuery<
    LeaderboardFundraisersListQuery
  >(DOCUMENT, {
    notifyOnNetworkStatusChange: true,
  });

  if (
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.refetch ||
    error
  ) {
    return (
      <PageSection title={t('fundraisers.title', 'Fundraisers')} hasBorder>
        <BenefactorListItemSkeleton animated={false} />
      </PageSection>
    );
  }

  const fundraisers = data?.page?.fundraisers;

  if (!fundraisers || !data?.page?.allowFundraising) {
    return null;
  }

  async function handleFetchMore() {
    await fetchMore({
      variables: {
        after: fundraisers!.pageInfo.endCursor,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (
          !previousResult?.page?.fundraisers ||
          !fetchMoreResult?.page?.fundraisers
        ) {
          return previousResult;
        }

        return {
          page: {
            ...previousResult.page,
            fundraisers: {
              ...fetchMoreResult.page.fundraisers,
              edges: [
                ...previousResult.page.fundraisers.edges,
                ...fetchMoreResult.page.fundraisers.edges,
              ],
            },
          },
        };
      },
    });
  }

  return (
    <PageSection
      title={
        fundraisers.totalCount === 0
          ? t('fundraisers.noFundraisers', 'No fundraisers yet')
          : t('fundraisers.title', 'Fundraisers')
      }
      hasBorder
      count={fundraisers.totalCount}
      className={styles.container}
    >
      <ul data-qa="fundraisers-list">
        {fundraisers.edges.map(fundraiser => {
          const { donationSummary, ...fundraiserPage } = fundraiser.node;

          let totaliserValue: number | null = null;
          if (
            fundraiserPage.targetWithCurrency &&
            fundraiserPage.targetWithCurrency.value > 0
          ) {
            totaliserValue = Math.round(
              (donationSummary?.totalAmount?.value /
                fundraiserPage.targetWithCurrency.value) *
                100,
            );
          }

          return (
            <BenefactorListItem
              key={fundraiserPage.id}
              title={fundraiserPage.owner?.name}
              image={makePhotoUrlFromAbsoluteUrl(fundraiserPage.owner?.avatar)}
              currencyCode={donationSummary?.totalAmount?.currencyCode ?? 'GBP'}
              amount={
                donationSummary?.totalAmount?.value
                  ? donationSummary.totalAmount.value / 100
                  : 0
              }
              totaliserValue={totaliserValue}
              supporterCount={fundraiserPage.donations?.totalCount || 0}
              url={
                fundraiserPage.slug
                  ? urljoin(config.baseUri, fundraiserPage.slug)
                  : undefined
              }
              pageStatus={fundraiserPage.status as PageStatus}
            />
          );
        })}
      </ul>
      {fundraisers.pageInfo.hasNextPage && (
        <LoadMoreButton
          className="jg-btn"
          loading={loading}
          onClick={handleFetchMore}
          trackingData={{
            action_name: 'click',
            subtype: 'button',
            event: 'click',
            event_value: 'show more',
            sub_section: 'campaign members',
          }}
        >
          <Trans i18nKey="fundraisers.showMore">
            Show more
            <span className="jg-sr-only"> fundraisers</span>
          </Trans>
        </LoadMoreButton>
      )}
    </PageSection>
  );
};

export default LeaderboardFundraiserList;
